import { craftResolver } from "@/components/craft/resolver";

type Resolver = typeof craftResolver;

const LEGACY_BLOCK_ALIASES: Record<string, keyof Resolver> = {
  Text: "TextBlock",
  Hero: "HeroBlock",
  Container: "ContainerBlock",
  Image: "ImageBlock",
  Section: "SectionBlock",
  Row: "RowBlock",
  Column: "ColumnBlock",
  "Button link": "ButtonLinkBlock",
  "HTML embed": "HtmlEmbedBlock",
  Gallery: "GridGalleryBlock",
  "Icon list": "IconListBlock",
  Quote: "QuoteBlock",
  Card: "CardBlock",
  Video: "VideoBlock",
  Divider: "DividerBlock",
  Spacer: "SpacerBlock",
};

export function normalizeBlockKey(name: string | undefined | null): keyof Resolver | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  if (trimmed in craftResolver) return trimmed as keyof Resolver;
  return LEGACY_BLOCK_ALIASES[trimmed] ?? null;
}

function resolveFromDisplayName(displayName: string | undefined): keyof Resolver | null {
  const trimmed = displayName?.trim();
  if (!trimmed) return null;

  const fromAlias = normalizeBlockKey(trimmed);
  if (fromAlias) return fromAlias;

  for (const [key, component] of Object.entries(craftResolver)) {
    if (component.craft?.displayName?.trim() === trimmed) {
      return key as keyof Resolver;
    }
  }

  return null;
}

/** Resolve a Craft node to its resolver key (e.g. TextBlock). */
export function resolveCraftBlockKey(
  node: {
    data: {
      name?: string;
      displayName?: string;
      type?: unknown;
    };
  },
): keyof Resolver | null {
  const { data } = node;

  const fromName = normalizeBlockKey(data.name);
  if (fromName) return fromName;

  const type = data.type;
  if (typeof type === "string") {
    const fromType = normalizeBlockKey(type);
    if (fromType) return fromType;
  }
  if (type && typeof type === "object" && "resolvedName" in type) {
    const fromResolved = normalizeBlockKey((type as { resolvedName: string }).resolvedName);
    if (fromResolved) return fromResolved;
  }
  if (typeof type === "function") {
    const craft = (type as { craft?: { name?: string; displayName?: string } }).craft;
    if (craft?.name) {
      const fromCraftName = normalizeBlockKey(craft.name);
      if (fromCraftName) return fromCraftName;
    }
    for (const [key, component] of Object.entries(craftResolver)) {
      if (component === type) return key as keyof Resolver;
    }
    if (craft?.displayName) {
      const fromCraftDisplay = resolveFromDisplayName(craft.displayName);
      if (fromCraftDisplay) return fromCraftDisplay;
    }
  }

  return resolveFromDisplayName(data.displayName);
}

export function craftBlockLabel(
  node: {
    data: {
      name?: string;
      displayName?: string;
      type?: unknown;
    };
  },
  fallback = "Block",
): string {
  const label = node.data.displayName?.trim();
  if (label) return label;
  const key = resolveCraftBlockKey(node);
  if (key) {
    const fromCraft = craftResolver[key]?.craft?.displayName?.trim();
    if (fromCraft) return fromCraft;
    return key.replace(/Block$/, "");
  }
  const fromName = normalizeBlockKey(node.data.name);
  if (fromName) {
    return craftResolver[fromName]?.craft?.displayName?.trim() || fromName.replace(/Block$/, "");
  }
  return fallback;
}

export function resolveCraftBlockSettings(resolvedName: keyof Resolver | null) {
  if (!resolvedName) return undefined;
  return craftResolver[resolvedName]?.craft?.related?.settings;
}
